// คำนวณยอดโอนของทีม (ค่ากด + รายได้พิเศษ) แบบ pure function
// ใช้ร่วมกันระหว่างหน้า "โอนค่ากด" ทุกส่วน (การ์ดทีม / รายละเอียดทีม / รอบโอน)
// เพื่อให้ทุกหน้าใช้รายการชุดเดียวกันและตัวเลขตรงกันเสมอ
import {
  calculateRecordFee,
  canonicalTeamName,
  getBangkokDateParts,
  isSalariedTeam,
} from "./feeRules";

export const SALARY_PAYDAY = 26;
export const SALARY_CUTOFF_DAY = 25;
export const EXTRA_WHT_RATE = 0.03;

// รายได้พิเศษที่บริษัทออกภาษีแทน "ตลอดไป" (gross-up)
// ทีมได้รับเต็มยอดที่กรอก (net) → เงินได้ที่ต้องหัก = net ÷ (1 − 3%)
// WHT = เงินได้ − net = net × 3/97  (เช่น 7,700 → เงินได้ 7,938.14, WHT 238.14)
export const EXTRA_WHT_METHOD = "company-borne-perpetual";
export const grossUpExtraIncome = (netAmount, rate = EXTRA_WHT_RATE) => {
  const net = roundMoney(netAmount);
  if (!(net > 0)) return { net: 0, gross: 0, wht: 0, whtBase: 0, whtRate: rate };
  const gross = roundMoney(net / (1 - rate));
  const wht = roundMoney(gross - net);
  return { net, gross, wht, whtBase: gross, whtRate: rate };
};

export const roundMoney = (value) =>
  Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

export const toDate = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (typeof value === "object" && typeof value.seconds === "number") {
    return new Date(value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6));
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const toTS = (value) => toDate(value)?.getTime() || 0;

export const endOfBangkokDay = (value = new Date()) => {
  const { year, month, day } = getBangkokDateParts(value);
  return new Date(
    `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T23:59:59.999+07:00`
  );
};

// งานวันที่ 1–25 → โอนวันที่ 26 ของเดือนนั้น
// งานวันที่ 26–สิ้นเดือน → โอนวันที่ 26 ของเดือนถัดไป
// ใช้วันตามเวลาไทยเสมอ
export const getSalaryPayday = (value) => {
  const date = toDate(value);
  if (!date) return null;
  let { year, month, day } = getBangkokDateParts(date);
  if (day > SALARY_CUTOFF_DAY) {
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return new Date(
    `${year}-${String(month).padStart(2, "0")}-${String(SALARY_PAYDAY).padStart(2, "0")}T23:59:59.999+07:00`
  );
};

// ใช้ลำดับเดียวกับ calculateRecordFee
export const recordDate = (record = {}) => record.createdAt || record.date || record.pressDate || null;
export const itemCreatedAt = (item = {}) => item.createdAt || item.date || null;

// รายได้พิเศษ: รายการใหม่ (taxBorneByCompany) = ยอดที่กรอกคือเงินที่ทีมได้รับเต็ม
// บริษัทออก WHT เพิ่มเอง → WHT ไม่ทำให้ยอดทีมลดลง
export const extraAmounts = (item = {}) => {
  const whtRate = Number.isFinite(Number(item.whtRate)) && item.whtRate !== null && item.whtRate !== ""
    ? Number(item.whtRate)
    : EXTRA_WHT_RATE;

  if (item.taxBorneByCompany) {
    const net = roundMoney(item.teamReceivesAmount ?? item.netAmount ?? item.amount ?? 0);
    if (item.whtMethod === EXTRA_WHT_METHOD && item.withholdingAmount == null && item.whtAmount == null) {
      const up = grossUpExtraIncome(net, whtRate);
      return { gross: up.gross, whtRate, wht: up.wht, net, whtBase: up.whtBase, taxBorneByCompany: true };
    }
    const whtBase = roundMoney(item.whtBaseAmount ?? net);
    const wht = roundMoney(item.withholdingAmount ?? item.whtAmount ?? whtBase * whtRate);
    const gross = roundMoney(item.companyCostAmount ?? net + wht);
    return { gross, whtRate, wht, net, whtBase, taxBorneByCompany: true };
  }

  // รายการรุ่นเก่า: บันทึกแบบ "ยอดก่อนหัก − WHT = ทีมได้รับ" → คงตัวเลขเดิมไว้ ไม่แก้ประวัติ
  const gross = roundMoney(item.grossAmount ?? item.amount ?? 0);
  const wht = roundMoney(item.withholdingAmount ?? item.whtAmount ?? gross * whtRate);
  const net = roundMoney(item.netAmount ?? gross - wht);
  return { gross, whtRate, wht, net, whtBase: gross, taxBorneByCompany: false };
};

// key ที่ไม่ซ้ำของแต่ละรายการ (records อยู่ใต้หลาย uid → ใช้ path เต็ม)
export const recordKey = (record = {}) => record.ref?.path || record.path || record.id;
export const extraKey = (item = {}) => item.ref?.path || item.path || item.id;

// รายการที่ยังไม่ถูกโอน: isPaid ไม่เป็น true และยังไม่ถูกผูกกับรอบโอนที่ยืนยันแล้ว
export const isOutstanding = (item = {}) => item.isPaid !== true && !item.settlementId;

// ตัดรายการซ้ำ (เช่น doc เดียวกันถูกดึงมามากกว่า 1 ครั้ง) ด้วย path
export const uniqueBy = (list = [], keyFn) => {
  const seen = new Set();
  const result = [];
  list.forEach((item) => {
    const key = keyFn(item);
    if (key && seen.has(key)) return;
    if (key) seen.add(key);
    result.push(item);
  });
  return result;
};

const sumPress = (list) =>
  list.reduce(
    (acc, record) => {
      const fee = calculateRecordFee(record);
      acc.baseFee += Number(fee.baseFee || 0);
      acc.commission += Number(fee.deducted || 0);
      acc.beforeWht += Number(fee.teamBeforeTax || 0);
      acc.wht += Number(fee.tax || 0);
      acc.net += Number(fee.net || 0);
      acc.quantity += Number(record.quantity || 0);
      acc.count += 1;
      return acc;
    },
    { baseFee: 0, commission: 0, beforeWht: 0, wht: 0, net: 0, quantity: 0, count: 0 }
  );

const sumExtra = (list) =>
  list.reduce(
    (acc, item) => {
      const amounts = extraAmounts(item);
      acc.gross += amounts.gross;
      acc.wht += amounts.wht;
      acc.net += amounts.net;
      acc.count += 1;
      return acc;
    },
    { gross: 0, wht: 0, net: 0, count: 0 }
  );

const roundAll = (obj) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      typeof value === "number" && !["count", "quantity"].includes(key) ? roundMoney(value) : value,
    ])
  );

// สรุปยอดของรายการชุดหนึ่ง (ค่ากด + รายได้พิเศษ)
export const summarizeItems = (pressList = [], extraList = []) => {
  const press = roundAll(sumPress(pressList));
  const extra = roundAll(sumExtra(extraList));
  return {
    pressList,
    extraList,
    press,
    extra,
    pressNet: press.net,
    extraNet: extra.net,
    total: roundMoney(press.net + extra.net),
    count: press.count + extra.count,
    quantity: press.quantity,
  };
};

/**
 * คำนวณยอดของทีมหนึ่งทีมจากรายการที่ "ยังไม่โอน"
 *
 * outstanding = ค้างโอนทั้งหมด (ทุกรอบ)
 * round       = รายการที่จะโอนในรอบปัจจุบัน
 *               - ทีมทั่วไป: ทุกรายการค้าง
 *               - พนักงานเงินเดือน: เฉพาะ payday เก่าสุดที่ยังค้าง (ถ้าถึงกำหนดแล้ว = ยืนยันได้)
 *                 ถ้ายังไม่ถึงกำหนด = รอบถัดไปที่เตรียมล่วงหน้าได้
 * remaining   = outstanding − round (คงเหลือหลังรอบนี้)
 */
export const buildTeamPayout = ({ team, records = [], extras = [], now = new Date() } = {}) => {
  const teamName = canonicalTeamName(team);
  const salaried = isSalariedTeam(teamName);
  const todayEnd = endOfBangkokDay(now);

  const pressOutstanding = uniqueBy(records, recordKey).filter(isOutstanding);
  const extraOutstanding = uniqueBy(extras, extraKey).filter(isOutstanding);

  const outstanding = summarizeItems(pressOutstanding, extraOutstanding);

  let roundDate = null;
  let roundPress = pressOutstanding;
  let roundExtra = extraOutstanding;
  let canConfirm = pressOutstanding.length + extraOutstanding.length > 0;

  if (salaried) {
    const paydays = [
      ...pressOutstanding.map((record) => getSalaryPayday(recordDate(record))),
      ...extraOutstanding.map((item) => getSalaryPayday(itemCreatedAt(item))),
    ].filter(Boolean).sort((a, b) => a - b);

    roundDate = paydays[0] || null;
    const roundTs = roundDate?.getTime();
    // รายการที่ไม่มีวันที่เลยถือว่าถึงกำหนดแล้วและเข้ารอบเก่าสุด (กันเงินค้างหาย)
    roundPress = pressOutstanding.filter((record) => {
      const payday = getSalaryPayday(recordDate(record));
      return !payday || payday.getTime() === roundTs;
    });
    roundExtra = extraOutstanding.filter((item) => {
      const payday = getSalaryPayday(itemCreatedAt(item));
      return !payday || payday.getTime() === roundTs;
    });
    canConfirm = !!roundDate && roundDate <= todayEnd && roundPress.length + roundExtra.length > 0;
    if (!roundDate && roundPress.length + roundExtra.length > 0) canConfirm = true;
  }

  const round = summarizeItems(roundPress, roundExtra);
  const roundKeys = new Set([
    ...roundPress.map(recordKey),
    ...roundExtra.map(extraKey),
  ]);
  const remaining = summarizeItems(
    pressOutstanding.filter((record) => !roundKeys.has(recordKey(record))),
    extraOutstanding.filter((item) => !roundKeys.has(extraKey(item)))
  );

  const remainingPaydays = [
    ...remaining.pressList.map((record) => getSalaryPayday(recordDate(record))),
    ...remaining.extraList.map((item) => getSalaryPayday(itemCreatedAt(item))),
  ].filter(Boolean).sort((a, b) => a - b);

  return {
    team: teamName,
    salaried,
    outstanding,
    round: {
      ...round,
      date: roundDate,
      canConfirm,
      preparingAhead: salaried && !canConfirm && round.count > 0,
    },
    remaining: {
      ...remaining,
      nextDate: salaried ? remainingPaydays[0] || null : null,
    },
  };
};

// ตรวจก่อนสร้าง/ยืนยันรอบโอน: รายการเดียวกันต้องไม่อยู่ในรอบโอนอื่นที่ยืนยันแล้ว
export const findAlreadySettled = ({ pressPaths = [], extraIds = [], settlements = [], excludeSettlementId = null } = {}) => {
  const pressSet = new Set(pressPaths.filter(Boolean));
  const extraSet = new Set(extraIds.filter(Boolean));
  const conflicts = [];
  settlements
    .filter((settlement) => settlement.status === "paid" && settlement.id !== excludeSettlementId)
    .forEach((settlement) => {
      (settlement.pressRecordPaths || []).forEach((path) => {
        if (pressSet.has(path)) conflicts.push({ type: "press", key: path, settlementId: settlement.id });
      });
      (settlement.extraIncomeIds || []).forEach((id) => {
        if (extraSet.has(id)) conflicts.push({ type: "extra", key: id, settlementId: settlement.id });
      });
    });
  return conflicts;
};

// ---------------------------------------------------------------------------
// เงินเดือนขั้นต่ำของพนักงานเงินเดือน (ยืนยัน 26 ต.ค. 2569)
// พนักงานเงินเดือนได้ "เงินเดือน 15,000" เป็นขั้นต่ำ ถ้าค่ากด + รายได้พิเศษในรอบไม่ถึง
// บริษัทจ่ายส่วนที่ขาดให้เต็ม → เป็นค่าจ้างและค่าใช้จ่ายบริษัท ไม่มีการโอนคืน
//   เงินช่วย = max(0, 15,000 − ยอดที่ทำได้ในรอบ)
// เช่น บอตอทำได้ 10,425 → บริษัทจ่าย 4,575 → ค่าจ้าง 15,000 − ประกันสังคม 750 = โอน 14,250
export const SALARY_FLOOR_DEFAULT = 15000;
export const SALARY_FLOOR_BY_TEAM = {
  // ใส่ค่าเฉพาะคนได้ถ้าเงินเดือนไม่เท่ากัน เช่น "บต": 15000
};

export const getSalaryFloor = (teamName) => {
  const team = canonicalTeamName(teamName);
  if (!isSalariedTeam(team)) return 0;
  return Number(SALARY_FLOOR_BY_TEAM[team] ?? SALARY_FLOOR_DEFAULT);
};

export const calcSalarySupport = (teamName, earnings = 0) => {
  const floor = getSalaryFloor(teamName);
  if (!floor) return 0;
  return roundMoney(Math.max(0, floor - Number(earnings || 0)));
};

// ประกันสังคมส่วนลูกจ้าง 5% ของค่าจ้าง (ฐานต่ำสุด 1,650 สูงสุด 15,000) ปัดเป็นบาท
// ใช้เป็นค่าเริ่มต้นในฟอร์มรอบโอนเท่านั้น แอดมินแก้ได้เสมอ
export const SOCIAL_SECURITY_RATE = 0.05;
export const SOCIAL_SECURITY_WAGE_MIN = 1650;
export const SOCIAL_SECURITY_WAGE_MAX = 15000;
export const calcSocialSecurity = (wage = 0) => {
  const amount = Number(wage || 0);
  if (!(amount > 0)) return 0;
  const base = Math.min(SOCIAL_SECURITY_WAGE_MAX, Math.max(SOCIAL_SECURITY_WAGE_MIN, amount));
  return Math.round(base * SOCIAL_SECURITY_RATE);
};

// ---------------------------------------------------------------------------
// กติกาหลัก (ยืนยัน 26 ต.ค. 2569): ถ้าโอนให้ทีม "เกิน" จากที่ทีมทำได้ในรอบ
// ส่วนที่เกิน = ค่าใช้จ่ายบริษัท (บริษัทจ่ายเพิ่มให้ทีม)
//   ค่าจ้างที่จ่ายจริง = ยอดโอนจริง + WHT ที่หักไว้ + ประกันสังคมที่หักไว้
//   บริษัทจ่ายเพิ่ม     = max(0, ค่าจ้างที่จ่ายจริง − ยอดที่ทีมทำได้ในรอบ)
//   ถ้าโอนน้อยกว่าที่ทำได้ → ไม่ใช่ค่าใช้จ่าย แต่เป็น "ยอดจ่ายขาด" ที่ต้องเช็ก
// คำนวณจากตัวเลขในรอบโอนเสมอ → รอบเก่าที่ไม่เคยบันทึกเงินช่วยก็ได้ค่าที่ถูกโดยไม่ต้องแก้ข้อมูล
export const calcCompanyTopUp = ({ systemDueAmount = 0, actualTransferAmount = 0, payrollWhtAmount = 0, socialSecurityAmount = 0 } = {}) => {
  const paidWage = Number(actualTransferAmount || 0) + Number(payrollWhtAmount || 0) + Number(socialSecurityAmount || 0);
  const diff = roundMoney(paidWage - Number(systemDueAmount || 0));
  // เศษสตางค์จากการปัดยอดโอน (เช่น 0.25) ไม่นับ
  if (Math.abs(diff) < 1) return { topUp: 0, shortfall: 0, paidWage: roundMoney(paidWage) };
  return {
    topUp: diff > 0 ? diff : 0,
    shortfall: diff < 0 ? roundMoney(-diff) : 0,
    paidWage: roundMoney(paidWage),
  };
};
